package dev.kuml.portal.web.components

import dev.kuml.portal.web.AppScope
import dev.kuml.portal.web.api.ApiResult
import dev.kuml.portal.web.api.PortalApi
import dev.kuml.portal.web.i18n.t
import dev.kuml.portal.web.usage.usageChip
import io.kvision.core.Container
import io.kvision.core.onClick
import io.kvision.html.button
import io.kvision.html.div
import io.kvision.html.image
import kotlinx.coroutines.launch

/** @property refreshUsage triggers a re-fetch of the usage chip -- called by the editor right after
 * a successful diagram generation, so the design spec's "Nutzungs-Indikator zaehlt sichtbar hoch"
 * happens without waiting for the next screen navigation.
 * @property refreshIdentity re-fetches and rebuilds the header's own avatar (R-23) -- called by
 * the profile screen right after a successful displayName save, so the header's initials/avatar
 * (built once from `GET /api/auth/me` at mount, see below) don't keep showing the old name until
 * the next full navigation. */
public class PortalHeader(
    public val refreshUsage: () -> Unit,
    public val refreshIdentity: () -> Unit,
)

/**
 * The global authenticated-screen header (design spec: "Kopfzeile mit kUML-Wortmarke links,
 * Nutzungs-Indikator + direkt sichtbarem sekundaeren Text-Button 'Abmelden' rechts"). Identical on
 * `/projects` and every `/projects/:id` -- callers add their own screen-local sub-header below it.
 *
 * @param onWordmarkClick navigates to `/projects` -- the wordmark (now the kUML logo) is always a
 *   way home.
 * @param onLoggedOut called after `POST /api/auth/logout` completes (successfully or not -- the
 *   client-side session is over either way once the cookie-clearing request has been sent).
 * @param onImpressumClick / @param onDatenschutzClick / @param onAgbClick navigate to the three
 *   legally required pages (§ 5 TMG / Art. 13 DSGVO / AGB) -- kept reachable from every
 *   authenticated screen, not just the auth screen, since [legalLinks] renders the same row here
 *   and there.
 * @param onProfileClick navigates to `/profile` (R-16) -- the small avatar shown before the
 *   "Abmelden" button is the only entry point into the profile screen.
 */
public fun Container.portalHeader(
    api: PortalApi,
    onWordmarkClick: () -> Unit,
    onLoggedOut: () -> Unit,
    onImpressumClick: () -> Unit,
    onDatenschutzClick: () -> Unit,
    onAgbClick: () -> Unit,
    onProfileClick: () -> Unit,
): PortalHeader {
    lateinit var chipRefresh: () -> Unit
    lateinit var identityRefresh: () -> Unit
    div(className = "kportal-header") {
        div(className = "kportal-header-left") {
            val logo =
                div(className = "kportal-header-logo") {
                    image(src = "img/logo.svg", alt = "kUML").addCssClass("kportal-logo-light")
                    image(src = "img/logo-negative.svg", alt = "kUML").addCssClass("kportal-logo-dark")
                }
            logo.onClick { onWordmarkClick() }
        }
        div(className = "kportal-header-right") {
            chipRefresh = usageChip(api).refresh
            legalLinks(onImpressumClick, onDatenschutzClick, onAgbClick)
            // R-18: seiten-bezogenes Meta-Element wie legalLinks -- direkt davor, vor dem
            // konto-bezogenen Avatar/"Abmelden" (siehe dieser Welle's Plan §4.2).
            languageSwitcher()
            // R-16: the header's own avatar -- loaded once via GET /api/auth/me (independent of
            // the profile screen, which fetches it again itself). Silent-fail like usageChip
            // above: this is a purely informational nav affordance, so a network hiccup just
            // leaves it blank rather than an alarming error in the header.
            //
            // R-23: pulled into a named `loadIdentity()` (instead of an inline `AppScope.launch{}`)
            // and re-invoked as [PortalHeader.refreshIdentity] -- this avatar was previously built
            // exactly once per mount, so a rename left it showing stale initials until the next
            // full navigation. `removeAll()` first, mirroring `ProfileScreen`'s own rebuild
            // discipline (a rename changes the initials fallback, which `AvatarWidget.refresh`
            // alone cannot pick up).
            val avatarSlot = div(className = "kportal-header-avatar")
            avatarSlot.onClick { onProfileClick() }

            fun loadIdentity() {
                avatarSlot.removeAll()
                AppScope.launch {
                    val result = api.me()
                    if (result is ApiResult.Success) {
                        val user = result.value
                        avatarSlot.avatarWidget(user.id, user.displayName, user.avatarUpdatedAt, "kportal-avatar-sm")
                    }
                }
            }
            identityRefresh = ::loadIdentity
            loadIdentity()
            button(
                text = t.header.logout,
                className = "kportal-btn kportal-btn-secondary kportal-btn-auto",
            ).onClick {
                AppScope.launch {
                    api.logout()
                    onLoggedOut()
                }
            }
        }
    }
    return PortalHeader(refreshUsage = chipRefresh, refreshIdentity = identityRefresh)
}
